import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, take } from "rxjs/operators";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { Address, Availability, Location } from "../../../classes";
import { IAvailabilityProviderService, ILocationProviderService } from "../../../interfaces";
import { LocationService } from "../../../services/location.service";

@Injectable()
export class LocationDetailViewComponentService implements ILocationProviderService, IAvailabilityProviderService {

    private _loading = new Subject<boolean>();
    loading$ = this._loading.asObservable();

    private _locationId = new ReplaySubject<number>(1);
    locationId$: Observable<number> = this._locationId.asObservable();

    private _location = new ReplaySubject<Location>(1);
    location$: Observable<Location> = this._location.asObservable();

    availabilities$ = this._locationId.pipe(switchMap((locationId: number) => this._locationService.getLocationAvailabilities(locationId, true)));

    private _currentLocationSub: Subscription = null;
    private _subscriptions: Subscription[] = [];

    constructor(
        private _route: ActivatedRoute,
        private _locationService: LocationService,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.setUp();
    }

    deleteCurrentLocation(): Observable<Object> {
        let subject = new Subject();
        this.locationId$
            .pipe(take(1), switchMap((locationId: number) => this._locationService.deleteLocation(locationId)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    dispose(): void {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentLocation();
    }

    refreshCurrentLocation(): Observable<Location> {
        let subject = new Subject<Location>();
        this._locationId.pipe(
            take(1),
            switchMap(x => this._locationService.getLocationById(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    saveChanges(location: Location, note: string, address: Address, availabilities: Availability[]): Observable<Location> {

        return (address ? this._locationService[(typeof address.addressid === 'number' && address.addressid !== 0) ? 'updateAddress' : 'createAddress'](address) : of(address))
            .pipe(
                switchMap((address: Address) => {
                    location.locationaddress = address.addressid;
                    return this._locationService.updateLocation(location, note).pipe(
                        switchMap((result: Location) => {
                            if (availabilities.length > 0) {
                                availabilities.forEach(x => x.availabilitylocation = result.locationid);
                                return combineLatest(availabilities.map(x => {
                                    if(x.availabilityid){
                                        if(typeof(x.availabilitystate) !== 'number') return this._locationService.deleteAvailability(x.availabilityid);
                                        return this._locationService.updateAvailability(x);
                                    } else return this._locationService.createAvailability(x);
                                })).pipe(map(() => result));
                            }
                            else return of(result);
                        })
                    );
                })
            );
    }

    setLocationId(locationId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentLocation();
        this._currentLocationSub = this._locationService.getLocationById(locationId, false).subscribe({
            next: (location: Location) => {
                this._location.next(location);
                this._loading.next(false);
            },
            error: (error: HttpErrorResponse) => {
                switch(error.status){
                    case 404:
                        this._dialog.open(ErrorDialogComponent, {
                            panelClass: 'no-padding-dialog-container',
                            disableClose: true,
                            data: {
                                'description': `Es konnte kein Sztandort mit der ID ${locationId} gefunden werden. Ist der Link eventuell veraltet oder wurde der Standort in der Zwischenzeit gelöscht?`,
                                'error': error,
                                'showNavigateToMainPage': true
                            } as ErrorWrapper
                        });
                        break;
                }
                this._loading.next(false);
            }
        });
    }

    setUp(): void {
        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((issueId: number) => this._locationId.next(issueId)),
            this.locationId$.pipe(distinctUntilChanged()).subscribe((identifier) => this.setLocationId(identifier))
        ]);
    }

    updateIssue(location: Location) {
        if (location) {
            this._location.next(location);
            this._locationId.next(location.locationid);
        }
    }

    validateAvailabilities = () => this._locationId.pipe(switchMap((locationId) => this._locationService.getLocationAvailabilities(locationId, false)));

    private _unsubscribeCurrentLocation() {
        if (this._currentLocationSub) {
            this._currentLocationSub.unsubscribe();
            this._currentLocationSub = null;
        }
    }
}